// @file To run Blockly scripts in the whiteboard.
// @fileoverview
// This file contains the functions to run Blockly scripts in the whiteboard.
// Blockly scripts are converted to JavaScript code and run using the js-interpreter library.

import { navigateTo } from '@@/bits/location'
import type { BlocklyScriptBlocks } from '@@/types/whiteboard'
import Blockly from 'blockly/core'
import { javascriptGenerator } from 'blockly/javascript'
import Interpreter from 'js-interpreter'

/**
 * A class that represents a destination for the Stacks.linkTo function
 * @usage Stacks.linkTo(Stacks.frameNumber(1))
 * translates to (new StackFrame(1)).go()
 */
abstract class StackDestination {
  abstract go(): void
}

/**
 * Stacks.frameNumber(x) - A destination that links to a specific frame number
 */
class StackFrame extends StackDestination {
  frameNumber: number
  constructor(frameNumber: number) {
    super()
    this.frameNumber = frameNumber
  }

  go(): void {
    // TODO: go to the frame number once frame is implemented
  }
}

/**
 * Stacks.url(x) - A destination that links to a specific URL
 */
class StackUrl extends StackDestination {
  urlString: string
  constructor(urlString: string) {
    super()
    this.urlString = urlString
  }

  go(): void {
    navigateTo(this.urlString, { target: 'stacks_link_to_url' }) // Set target as a string to prevent loops from opening multiple tabs
  }
}

function initApi(interpreter: Interpreter, globalObject: any): void {
  const Stacks = interpreter.nativeToPseudo({})
  interpreter.setProperty(globalObject, 'Stacks', Stacks)

  interpreter.setProperty(
    Stacks,
    'linkTo',
    interpreter.createNativeFunction((destination: StackDestination) => {
      destination.go()
    }),
  )
  interpreter.setProperty(
    Stacks,
    'frameNumber',
    interpreter.createNativeFunction((frameNumber: number) => {
      return new StackFrame(frameNumber)
    }),
  )
  interpreter.setProperty(
    Stacks,
    'url',
    interpreter.createNativeFunction((urlString) => {
      return new StackUrl(urlString)
    }),
  )
}

function generateJsFromBlocklyScriptBlocks(script: BlocklyScriptBlocks): string {
  // Create an invisible blockly workspace, and load the json script into it
  const workspace = new Blockly.Workspace()
  const json = JSON.parse(script)
  Blockly.serialization.workspaces.load(json, workspace)
  // Generate javascript code from the blockly workspace
  return javascriptGenerator.workspaceToCode(workspace)
}

const RUN_CLICK_SCRIPT = `mouseClickListener();\n`

function runScript(script: BlocklyScriptBlocks, eventName: 'click'): void {
  if (script == null) {
    return
  }
  if (eventName == null) {
    return
  }
  let executeScript: string
  switch (eventName) {
    case 'click':
      executeScript = RUN_CLICK_SCRIPT
      break
  }
  const generatedJs = generateJsFromBlocklyScriptBlocks(script)
  const fullScript = `${generatedJs}\n${executeScript}`
  const intepreter = new Interpreter(fullScript, initApi)

  intepreter.run()
}

export { runScript }
